import React from "react";
import { Typography } from "@material-tailwind/react";
import { 
  ShieldCheckIcon, 
  ClockIcon, 
  CurrencyDollarIcon, 
  UserGroupIcon,
  ChartBarIcon,
  GlobeAltIcon
} from "@heroicons/react/outline";

const About = () => {
  const stats = [
    {
      number: "5,000+",
      label: "Active Businesses",
      icon: UserGroupIcon,
    },
    {
      number: "50,000+",
      label: "Successful Transactions",
      icon: ChartBarIcon,
    },
    {
      number: "24/7",
      label: "Customer Support",
      icon: ClockIcon,
    },
    {
      number: "98%",
      label: "Satisfaction Rate",
      icon: ShieldCheckIcon,
    },
  ];

  const features = [
    {
      icon: ShieldCheckIcon,
      title: "Verified Suppliers Only",
      description: "Every vendor is thoroughly vetted and rated. No more dealing with unreliable suppliers or quality issues.",
      color: "bg-green-100 text-green-600",
    },
    {
      icon: ClockIcon,
      title: "Real-time Opportunity Alerts",
      description: "Get instant notifications when opportunities match your business. Be first to respond, not last to hear.",
      color: "bg-blue-100 text-blue-600",
    },
    {
      icon: CurrencyDollarIcon,
      title: "Guaranteed Cost Savings",
      description: "Multiple competitive quotes ensure you never overpay. Average 40% savings vs traditional procurement.",
      color: "bg-orange-100 text-orange-600",
    },
    {
      icon: GlobeAltIcon,
      title: "Nigeria-Wide Coverage",
      description: "Connect with businesses across all states in Nigeria. Local expertise with national reach.",
      color: "bg-purple-100 text-purple-600",
    },
  ];

  return (
    <section id="about" className="py-20 lg:py-32 bg-white">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 bg-orange-100 text-orange-700 rounded-full text-sm font-medium mb-6">
            <span className="w-2 h-2 bg-orange-500 rounded-full mr-2"></span>
            About Twerce
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            The B2B Marketplace That Actually Works
          </h2>
          <Typography
            variant="p"
            className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed"
            style={{ fontFamily: 'Open Sans, sans-serif' }}
          >
            While others promise, we deliver. Twerce connects verified buyers with qualified suppliers, 
            eliminating the pain of traditional procurement while delivering real results: 40% cost savings, 
            70% faster deal closure, and 98% satisfaction rate.
          </Typography>
        </div>

        {/* Stats Section */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
          {stats.map((stat, index) => (
            <div key={index} className="text-center group">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-2xl mb-4 group-hover:bg-CC5500 group-hover:text-white transition-all duration-300">
                <stat.icon className="w-8 h-8" />
              </div>
              <div className="text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
                {stat.number}
              </div>
              <div className="text-gray-600 font-medium" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                {stat.label}
              </div>
            </div>
          ))}
        </div>

        {/* Features Grid */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Content */}
          <div>
            <h3 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-8">
              Why Businesses Choose Twerce
            </h3>
            <Typography
              variant="p"
              className="text-lg text-gray-600 mb-8 leading-relaxed"
              style={{ fontFamily: 'Open Sans, sans-serif' }}
            >
              Traditional procurement is broken. Endless supplier calls, unreliable vendors, and missed opportunities 
              cost businesses time and money. Twerce fixes these problems with a platform that actually works.
            </Typography>
            
            <div className="space-y-6">
              {features.map((feature, index) => (
                <div key={index} className="flex items-start space-x-4 group">
                  <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center ${feature.color} group-hover:scale-110 transition-transform duration-200`}>
                    <feature.icon className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="text-xl font-semibold text-gray-900 mb-2">
                      {feature.title}
                    </h4>
                    <p className="text-gray-600 leading-relaxed" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                      {feature.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Visual Element */}
          <div className="relative">
            <div className="bg-gradient-to-br from-orange-50 to-blue-50 rounded-3xl p-8 lg:p-12">
              <div className="grid grid-cols-2 gap-6">
                <div className="bg-white rounded-2xl p-6 shadow-lg">
                  <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mb-4">
                    <UserGroupIcon className="w-6 h-6 text-orange-600" />
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2">Buyers</h4>
                  <p className="text-sm text-gray-600" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                    Post requirements and receive competitive quotes
                  </p>
                </div>
                
                <div className="bg-white rounded-2xl p-6 shadow-lg">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                    <ChartBarIcon className="w-6 h-6 text-blue-600" />
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2">Suppliers</h4>
                  <p className="text-sm text-gray-600" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                    Find new opportunities and grow your business
                  </p>
                </div>
                
                <div className="bg-white rounded-2xl p-6 shadow-lg">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4">
                    <ShieldCheckIcon className="w-6 h-6 text-green-600" />
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2">Verified</h4>
                  <p className="text-sm text-gray-600" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                    All businesses are thoroughly vetted
                  </p>
                </div>
                
                <div className="bg-white rounded-2xl p-6 shadow-lg">
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-4">
                    <GlobeAltIcon className="w-6 h-6 text-purple-600" />
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2">Nigeria-Wide</h4>
                  <p className="text-sm text-gray-600" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                    Connect with businesses across Nigeria
                  </p>
                </div>
              </div>
            </div>
            
            {/* Floating Elements */}
            <div className="absolute -top-6 -right-6 w-24 h-24 bg-orange-200 rounded-full opacity-20 animate-pulse"></div>
            <div className="absolute -bottom-6 -left-6 w-20 h-20 bg-blue-200 rounded-full opacity-20 animate-pulse delay-1000"></div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-20 text-center">
          <div className="bg-gradient-to-r from-CC5500 to-orange-600 rounded-3xl p-8 lg:p-12 text-white">
            <h3 className="text-3xl lg:text-4xl font-bold mb-4">
              Stop Losing Money to Inefficient Procurement
            </h3>
            <p className="text-xl mb-8 opacity-90" style={{ fontFamily: 'Open Sans, sans-serif' }}>
              Join 5,000+ businesses already saving time and money with Twerce across Nigeria. Your competitors are already here.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-white text-CC5500 px-8 py-4 rounded-xl font-semibold text-lg hover:bg-gray-50 transition-colors duration-200">
                Start Saving Today
              </button>
              <button className="border-2 border-white text-white px-8 py-4 rounded-xl font-semibold text-lg hover:bg-white hover:text-CC5500 transition-all duration-200">
                See Real Results
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
